import { Dispatch } from 'react';
import { AdvisorId } from '../../../api/Advisors/types/Advisor';
import { statuses } from '../../../api/Advisors/types/Status';
import { TabItem } from '../../../components/Pages/Forms/Tabs';
import {
  FormItem,
  FormItem2,
} from '../../../components/common/Forms/types/FormItem';
import { getRoutePath, Pages } from '../../../router/constants';
import {
  AsyncValue,
  InvalidType,
  SubmittedAsyncValue,
  SubmittedValue,
  Type,
  ValidType,
  Value,
  ValueType,
} from '../../../utils/FormValue';
import { statusTitle } from '../Add/utils';
import * as Actions from '../Edit/types/Actions';
import { Item, Submitted, Valid } from './types/Item';
import { validators } from './validators';

export function validateField<T extends Value<any, any, any>>(
  validator: (v: ValueType<T>) => SubmittedValue<T>,
  v: T,
): SubmittedValue<T> {
  switch (v.__typeName) {
    case Type.initial:
      return validator(v.value);
    case Type.invalid:
    case Type.valid:
      return v as SubmittedValue<T>;
  }
}

export function validateAsyncField<T extends AsyncValue<any, any, any>>(
  validator: (v: ValueType<T>) => SubmittedAsyncValue<T>,
  v: T,
): SubmittedAsyncValue<T> {
  switch (v.__typeName) {
    case Type.initial:
    case Type.invalid:
      return validator(v.value);
    case Type.verifying:
    case Type.valid:
      return v as SubmittedAsyncValue<T>;
  }
}

export function validateItem(item: Item): Submitted {
  return {
    email: validateAsyncField(validators.email, item.email),
    firstName: validateAsyncField(validators.firstName, item.firstName),
    lastName: validateAsyncField(validators.lastName, item.lastName),
    birthDate: validateField(validators.birthDate, item.birthDate),
    logo: validateField(validators.logo, item.logo),
    country: validateField(validators.country, item.country),
    state: validateField(validators.state, item.state),
    city: validateField(validators.city, item.city),
    linkedIn: validateField(validators.linkedIn, item.linkedIn),
    long: validateField(validators.long, item.long),
    short: validateField(validators.short, item.short),
    address: validateField(validators.address, item.address),
    phone: validateField(validators.phone, item.phone),
    status: validateField(validators.status, item.status),
    specializations: validateField(
      validators.specializations,
      item.specializations,
    ),
    zip: validateField(validators.zip, item.zip),
    rate: validateField(validators.rate, item.rate),
  };
}

export function isValid(v: Submitted): v is Valid {
  return Object.values(v).every((i) => i.__typeName === 'valid');
}

export function createField<K extends keyof Item>(
  item: Item[K],
  key: K,
  dispatch: Dispatch<Actions.SetValue<K> | Actions.Toggle>,
): FormItem<ValueType<Item[K]>> {
  return {
    // @ts-expect-error, need to identify why compiler complains
    ...formValueToInput<ValidType<Item[K]>, InvalidType<Item[K]>>(item),
    onBlur: () => dispatch(Actions.toggle(key)),
    onChange: (value: ValueType<Item[K]>) =>
      dispatch(Actions.setValue({ key, value })),
  };
}

export function formValueToInput<T extends T2, T2>(
  t: Value<string, T, T2>,
): { value: T2; error: boolean; errorText?: string } {
  switch (t.__typeName) {
    case Type.initial:
    case Type.valid:
      return {
        value: t.value,
        error: false,
      };
    case Type.invalid:
      return {
        value: t.value,
        error: !!t.error,
        errorText: t.error !== 'Required' ? t.error : undefined,
      };
  }
}

export function formValueToInput2<T extends T2, T2>(
  t: AsyncValue<string, T, T2>,
): { value: T2; status: FormItem2<T>['status']; errorText?: string } {
  switch (t.__typeName) {
    case Type.initial:
      return {
        value: t.value,
        status: 'normal',
      };
    case Type.valid:
      return {
        value: t.value,
        status: 'success',
      };
    case Type.verifying:
      return {
        value: t.value,
        status: 'loading',
      };
    case Type.invalid:
      return {
        value: t.value,
        status: 'error',
        errorText: t.error.toLowerCase() !== 'required' ? t.error : undefined,
      };
  }
}

export const statusOptions = statuses.map((value) => ({
  label: statusTitle(value),
  value,
}));

export const getTabs = (
  id: AdvisorId | undefined,
  hasAccessToAction: (rule: string) => boolean,
): TabItem[] => [
  {
    title: 'Details',
    testId: 'details-tab',
    link: id
      ? getRoutePath(Pages.OH_ADVISORS_EDIT, { id })
      : Pages.OH_ADVISORS_NEW,
  },
  {
    title: 'Availabilities',
    link: getRoutePath(Pages.OH_ADVISORS_AVAILABILITIES, { id }),
    disabled: !id,
    testId: 'availabilities-tab',
  },
  ...(hasAccessToAction('advisor.logs.view')
    ? [
        {
          title: 'Logs',
          link: getRoutePath(Pages.OH_ADVISORS_LOGS, { id }),
          disabled: !id,
          testId: 'logs-tab',
        },
      ]
    : []),
];

export function matchLogo(slug: string, logos: string[]): string | undefined {
  return logos.find((logo) => logo.search(encodeURI(slug)) !== -1);
}
